import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getSupabaseUrl, getSupabaseAnonKey } from '@/lib/environments';
import { getAccessToken } from '@/lib/supabase';
import { supabase } from '@/lib/supabase';

/*
 * DEV AUTO-LOGIN
 * ---------------
 * When running locally (localhost or 192.168.*) we automatically sign in
 * as a pre-generated dev user so nobody has to keep clicking magic links
 * to iterate on the authenticated-only screens.
 *
 * Configuration lives in .env.local (gitignored) as:
 *   VITE_DEV_AUTO_LOGIN_USER_ID
 *   VITE_DEV_AUTO_LOGIN_ACCESS_TOKEN
 *   VITE_DEV_AUTO_LOGIN_REFRESH_TOKEN
 *
 * These are passed to supabase.auth.setSession() on mount when the
 * hostname matches a dev pattern. If the access token has expired,
 * the Supabase client auto-refreshes using the refresh token. If the
 * refresh token itself has expired, you'll see a console error and
 * need to regenerate the pair by re-running the magic-link / verify
 * flow against the dev project.
 *
 * SAFETY: the auto-login is gated on the hostname check below, so
 * it CANNOT fire on a deployed dev.mobile.kizu.online or any other
 * non-localhost host, even if the env vars somehow leak into a prod
 * build. Keep the vars out of .env.production.
 */
const DEV_AUTO_LOGIN_USER_ID = import.meta.env.VITE_DEV_AUTO_LOGIN_USER_ID as string | undefined;
const DEV_AUTO_LOGIN_ACCESS_TOKEN = import.meta.env.VITE_DEV_AUTO_LOGIN_ACCESS_TOKEN as string | undefined;
const DEV_AUTO_LOGIN_REFRESH_TOKEN = import.meta.env.VITE_DEV_AUTO_LOGIN_REFRESH_TOKEN as string | undefined;

function isLocalHost(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || !!h.match(/^192\.168\./) || !!h.match(/^10\./);
}

// Module-level guard so the auto-login POST only fires once per page load,
// even if MobileAuthGuard remounts from a route change.
let devAutoLoginAttempted = false;

interface MobileAuthGuardProps {
  children: React.ReactNode;
}

export function MobileAuthGuard({ children }: MobileAuthGuardProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [eventCheck, setEventCheck] = useState<{ checked: boolean; eventId: string | null }>({ checked: false, eventId: null });
  const [devLoginDone, setDevLoginDone] = useState(() => {
    // No dev auto-login needed if not localhost or no tokens configured
    if (!isLocalHost()) return true;
    if (!DEV_AUTO_LOGIN_ACCESS_TOKEN || !DEV_AUTO_LOGIN_REFRESH_TOKEN) return true;
    if (devAutoLoginAttempted) return true;
    return false;
  });

  // ?t=<token> handoff — closing-reminder email/SMS links land on
  // /mobile/event/<id>?t=<token>&tab=photos. Consume the token via
  // event-public-api/claim_token, verify the returned magiclink to
  // establish a session on this origin, then strip the param so a
  // refresh doesn't re-claim. Runs before the auth check below so the
  // user doesn't get bounced to /mobile/auth/login.
  const [tokenClaiming, setTokenClaiming] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return !!new URLSearchParams(window.location.search).get('t');
  });
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('t');
    if (!t) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${getSupabaseUrl()}/functions/v1/event-public-api`, {
          method: 'POST',
          headers: {
            apikey: getSupabaseAnonKey(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'claim_token', token: t }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.token_hash) {
          const { error: verifyErr } = await supabase.auth.verifyOtp({
            type: 'magiclink',
            token_hash: data.token_hash,
          });
          if (verifyErr) console.error('[MobileAuthGuard] verifyOtp error:', verifyErr.message);
        } else if (!res.ok) {
          console.error('[MobileAuthGuard] claim_token failed:', data?.error || res.status);
        }
      } catch (err) {
        console.error('[MobileAuthGuard] claim_token threw:', err);
      }
      params.delete('t');
      const nextSearch = params.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`,
      );
      if (!cancelled) setTokenClaiming(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // DEV AUTO-LOGIN: set the session from env tokens on localhost.
  // Fires exactly once per page load, before any of the real auth
  // checks below so AuthContext.user populates without a magic link.
  useEffect(() => {
    if (devAutoLoginAttempted) { setDevLoginDone(true); return; }
    if (!isLocalHost()) { setDevLoginDone(true); return; }
    if (!DEV_AUTO_LOGIN_ACCESS_TOKEN || !DEV_AUTO_LOGIN_REFRESH_TOKEN) { setDevLoginDone(true); return; }
    devAutoLoginAttempted = true;

    (async () => {
      try {
        const { data: existing } = await supabase.auth.getSession();
        // Respect any existing session, not just the configured dev user.
        // Previously we'd only skip when already signed in AS the dev
        // user, which meant any other user who landed on a /mobile/*
        // route on localhost (e.g. after navigating from the dashboard
        // banner into event mode) had their session overwritten with
        // the dev tokens — kicking them to the login screen.
        if (existing.session) {
          console.log('[DEV] Auto-login: already signed in as', existing.session.user.email, '— skipping');
          return;
        }
        const { error } = await supabase.auth.setSession({
          access_token: DEV_AUTO_LOGIN_ACCESS_TOKEN,
          refresh_token: DEV_AUTO_LOGIN_REFRESH_TOKEN,
        });
        if (error) {
          console.error('[DEV] Auto-login failed:', error.message, '(regenerate tokens in .env.local)');
        } else {
          console.log('[DEV] Auto-login: signed in as dev user');
        }
      } catch (err) {
        console.error('[DEV] Auto-login threw:', err);
      } finally {
        setDevLoginDone(true);
      }
    })();
  }, []);

  // Check if user has an active event (guest mode)
  useEffect(() => {
    if (!user || eventCheck.checked) return;
    // Don't check if already on an event page or viewing an asset
    // Never auto-redirect to event mode — users enter events explicitly
    setEventCheck({ checked: true, eventId: null });
    return;

    const check = async () => {
      try {
        const token = getAccessToken();
        if (!token) { setEventCheck({ checked: true, eventId: null }); return; }

        const res = await fetch(
          `${getSupabaseUrl()}/functions/v1/wall-api?action=my_event`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'apikey': getSupabaseAnonKey(),
            },
            body: '{}',
          }
        );
        const data = await res.json();
        if (data.has_event && data.event_id) {
          setEventCheck({ checked: true, eventId: data.event_id });
        } else {
          setEventCheck({ checked: true, eventId: null });
        }
      } catch {
        setEventCheck({ checked: true, eventId: null });
      }
    };

    check();
  }, [user, eventCheck.checked, location.pathname]);

  // DEV ONLY: Auto-login on localhost (disabled — see comment at top of file to re-enable)

  if (loading || !devLoginDone || tokenClaiming) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0f172a]">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/mobile/auth/login"
        state={{ returnUrl: location.pathname + location.search }}
        replace
      />
    );
  }

  // Waiting for event check
  if (!eventCheck.checked) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0f172a]">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Redirect to event mode if user has an active event
  if (eventCheck.eventId && !location.pathname.startsWith('/mobile/event/')) {
    return <Navigate to={`/mobile/event/${eventCheck.eventId}`} replace />;
  }

  return <>{children}</>;
}
